import { detectionProfileSchema, type DetectionProfile } from '@/features/detection/domain/profile'

// Brouillon du profil de calibration (fenêtre « Profil ») : valeurs saisies en texte (virgule ou point), validées par
// le même schéma que le profil du serveur.

export const profileFields = {
  captureRadiusM: 'Rayon de capture (m)',
  workRadiusM: 'Rayon de travail (m)',
  exitDistanceM: 'Distance de sortie (m)',
  maxAccuracyM: 'Précision max (m)',
  maxFixAgeS: 'Âge max du relevé (s)',
  maxSpeedMps: 'Vitesse max (m/s)',
  captureConfirmCount: 'Positions pour capturer',
  captureConfirmWindowS: 'Sur au moins (s)',
  minInterventionS: 'Durée minimale (s)',
  minMovingShare: 'Part en mouvement (0 à 1)',
  movementWindowS: 'Fenêtre de mouvement (s)',
  stationaryJitterM: 'Seuil d’immobilité (m)',
  exitConfirmCount: 'Positions pour sortir',
  exitConfirmWindowS: 'Sur au moins (s)',
  maxGapS: 'Trou max crédité (s)',
} as const

export type ProfileField = keyof typeof profileFields
export type ProfileDraft = Record<ProfileField, string>
export const radiusFields: ProfileField[] = ['captureRadiusM', 'workRadiusM', 'exitDistanceM']

export function draftOf(profile: DetectionProfile): ProfileDraft {
  return Object.fromEntries(
    (Object.keys(profileFields) as ProfileField[]).map((field) => [field, String(profile[field])]),
  ) as ProfileDraft
}

export type DraftResult = { ok: true; profile: DetectionProfile } | { ok: false; errors: string[] }

export function profileFromDraft(base: DetectionProfile, mode: DetectionProfile['mode'], draft: ProfileDraft): DraftResult {
  const errors: string[] = []
  const values: Partial<Record<ProfileField, number>> = {}
  for (const field of Object.keys(profileFields) as ProfileField[]) {
    const text = draft[field].trim().replace(',', '.')
    const value = Number(text)
    if (text === '' || !Number.isFinite(value)) errors.push(`${profileFields[field]} : nombre attendu`)
    else values[field] = value
  }
  if (errors.length > 0) return { ok: false, errors }
  const parsed = detectionProfileSchema.safeParse({
    ...base,
    ...values,
    mode,
    label: base.label.endsWith(' (modifié)') ? base.label : `${base.label} (modifié)`,
  })
  if (parsed.success) return { ok: true, profile: parsed.data }
  return {
    ok: false,
    errors: parsed.error.issues.map((issue) => {
      const field = issue.path[0]
      const label = typeof field === 'string' && field in profileFields ? profileFields[field as ProfileField] : field
      return `${String(label)} : ${issue.message}`
    }),
  }
}
